import React, { useState, useEffect } from 'react';
import { Modal, Stack, TextInput, MultiSelect, Group, Button, Text } from '@mantine/core';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { api } from '../../utils/api';
import { showToast } from '../../utils/toast';
import { getErrorMessage } from '../../utils/api';
import { CompetencyMap, Profession } from '../../types';
import { createEnterKeyHandler } from '../../utils/enterKeyHandler';

interface CompetencyMapCopyModalProps {
  opened: boolean;
  onClose: () => void;
  competencyMap: CompetencyMap | null;
}

export const CompetencyMapCopyModal: React.FC<CompetencyMapCopyModalProps> = ({
  opened,
  onClose,
  competencyMap,
}) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [name, setName] = useState('');
  const [selectedProfessions, setSelectedProfessions] = useState<string[]>([]);

  // Получение профессий для селекта
  const { data: professions = [] } = useQuery({
    queryKey: ['professions'],
    queryFn: async () => api('/professions'),
    staleTime: 5 * 60 * 1000,
  });

  // Мутация копирования
  const copyMapMutation = useMutation({
    mutationFn: async (data: { id: string; name: string; professions: string[] }) =>
      api(`/competency-maps/${data.id}/copy`, {
        method: 'POST',
        body: { name: data.name, professions: data.professions },
      }),
    onSuccess: () => {
      showToast({
        title: t('competencyMaps.copied'),
        message: '',
        color: 'teal',
      });
      queryClient.invalidateQueries({ queryKey: ['competency-maps'] });
      onClose();
    },
    onError: (error) => {
      const message = getErrorMessage(error, t('competencyMaps.copyError'));
      showToast({ title: t('common.error'), message, color: 'red' });
    },
  });

  // Инициализация формы при открытии модалки
  useEffect(() => {
    if (opened && competencyMap) {
      // Предзаполняем название с префиксом "Копия"
      setName(`${t('competencyMaps.copyPrefix')} ${competencyMap.name}`);
      setSelectedProfessions((competencyMap.professions || []).map((p) => p._id));
    }
  }, [opened, competencyMap, t]);

  const handleSave = () => {
    if (!name.trim() || selectedProfessions.length === 0 || !competencyMap) return;

    copyMapMutation.mutate({
      id: competencyMap._id,
      name: name.trim(),
      professions: selectedProfessions,
    });
  };

  const isLoading = copyMapMutation.isPending;

  return (
    <Modal opened={opened} onClose={onClose} title={t('competencyMaps.copyMap')} size="md">
      <Stack>
        <Text size="sm" c="dimmed">
          {t('competencyMaps.copyDescription')}
        </Text>

        <TextInput
          label={t('competencyMaps.newName')}
          placeholder={t('competencyMaps.newNamePlaceholder')}
          value={name}
          onChange={(e) => setName(e.target.value)}
          autoFocus
          onKeyDown={createEnterKeyHandler({ onSubmit: handleSave })}
          required
        />

        <MultiSelect
          label={t('competencyMaps.profession')}
          placeholder={t('competencyMaps.selectProfession')}
          data={professions.map((p: Profession) => ({ value: p._id, label: p.name }))}
          value={selectedProfessions}
          onChange={setSelectedProfessions}
          searchable
          required
        />

        <Group justify="flex-end" gap="sm">
          <Button variant="light" onClick={onClose} color="gray" disabled={isLoading}>
            {t('common.cancel')}
          </Button>
          <Button
            onClick={handleSave}
            color="teal"
            loading={isLoading}
            disabled={!name.trim() || selectedProfessions.length === 0}
          >
            {t('competencyMaps.copy')}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
};
